/* eslint-disable react-hooks/exhaustive-deps */
import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { GContextTypes } from '../../types';
import { GlobalContext } from '../../contexts';
import { Grid } from '@material-ui/core';
import {
  dublicateAlert,
  errorAlert,
  errorAlertMsg,
  successAlert,
} from '../common/funcs';
import PopupLayout from '../../pages/main/PopupLayout';
import TextFieldLocal from '../shared/TextFieldLocal';
import AutoFieldLocal from '../shared/AutoFieldLocal';
import CalenderLocal from '../../components/fields/CalenderLocal';
import { useReactToPrint } from 'react-to-print';
import PrintDownloadCustomer from './PrintDownloadCustomer';
import { CustomerClearancePrint } from '../../print';
import {
  buildCustomerFormNote,
  formTypeTitles,
  parseCustomerFormNote,
} from './customerFormMeta';

const amountSchema = yup
  .number()
  .transform((value, originalValue) => {
    if (
      originalValue === '' ||
      originalValue === null ||
      originalValue === undefined
    ) {
      return undefined;
    }
    return value;
  })
  .typeError('Amount must be a number')
  .positive('Amount must be greater than zero');

const getFormSchema = (formType: string) => {
  const isEntitlement = formType === 'entitlement';
  const isFinancialCompensation = formType === 'financialCompensation';
  const isClearanceWithDeposit = formType === 'clearanceWithDeposit';
  const isClearanceNoDeposit = formType === 'clearanceNoDeposit';

  return yup.object().shape({
    civilId: yup.string().trim().required('Civil ID is required'),
    agreementNo: isEntitlement
      ? yup.string().trim()
      : yup.string().trim().required('Agreement number is required'),
    amount:
      isFinancialCompensation || isClearanceWithDeposit
        ? amountSchema.required('Amount is required')
        : amountSchema,
    reason:
      isFinancialCompensation || isClearanceWithDeposit || isClearanceNoDeposit
        ? yup.string().trim().required('Reason is required')
        : yup.string().trim(),
    iban:
      isFinancialCompensation || isClearanceWithDeposit
        ? yup.string().trim().required('IBAN is required')
        : yup.string().trim(),
    deliveryDetails: yup.string().trim(),
    extraNotes: yup.string().trim(),
  });
};

const toInputDate = (value: any) => {
  if (!value) return null;
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  const year = date.getFullYear();
  const month = `${date.getMonth() + 1}`.padStart(2, '0');
  const day = `${date.getDate()}`.padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const toJSDate = (value: any) => {
  if (!value) return null;
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }

  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
    const [year, month, day] = value.split('-').map(Number);
    return new Date(year, month - 1, day, 12, 0, 0, 0);
  }

  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

const PopupCustomerForm = ({
  open,
  onClose,
  row,
  isNew,
  addAction,
  editAction,
  theme,
  words,
  customers,
  opType,
  formType,
  company,
  onSaved,
}: any) => {
  const [saving, setSaving] = useState(false);
  const [custvalue, setCustvalue] = useState<any>(null);
  const [eventDate, setEventDate] = useState<any>(null);
  const [documentDate, setDocumentDate] = useState<any>(null);
  const [alrt, setAlrt] = useState({ show: false, msg: '', type: undefined });

  const isEntitlement = formType === 'entitlement';
  const isFinancialCompensation = formType === 'financialCompensation';
  const isClearanceWithDeposit = formType === 'clearanceWithDeposit';
  const isClearanceNoDeposit = formType === 'clearanceNoDeposit';
  const requiresEventDate =
    isEntitlement || isFinancialCompensation || isClearanceWithDeposit;

  const customerResolver = useMemo(
    () => ({ resolver: yupResolver(getFormSchema(formType)) }),
    [formType],
  );

  const { register, handleSubmit, errors, reset, setValue, getValues } =
    useForm(customerResolver);
  const {
    translate: { isRTL },
  }: GContextTypes = useContext(GlobalContext);

  const componentRef: any = useRef();
  const handleReactPrint = useReactToPrint({
    content: () => componentRef.current,
    documentTitle: `Customer Form #${row?.docNo}`,
    removeAfterPrint: true,
  });

  useEffect(() => {
    if (!open) {
      return;
    }

    const today = toInputDate(new Date());

    if (isNew) {
      reset({});
      setCustvalue(null);
      setEventDate(null);
      setDocumentDate(today);
      return;
    }

    if (!row?._id) {
      return;
    }

    const cust = customers.find((c: any) => c._id === row.customerId);
    const meta = parseCustomerFormNote(row.note);
    const fallbackReason = !isEntitlement ? row?.desc || '' : '';

    setCustvalue(cust);
    setEventDate(toInputDate(meta?.eventDate || row.startDate));
    setDocumentDate(toInputDate(row.time) || today);

    setValue('civilId', meta?.civilId || cust?.nationalNo || '');
    setValue('agreementNo', meta?.agreementNo || row?.title || '');
    setValue('amount', row?.amount);
    setValue('reason', meta?.reason || fallbackReason);
    setValue('iban', meta?.iban || '');
    setValue(
      'deliveryDetails',
      meta?.deliveryDetails || (isEntitlement ? row?.desc || '' : ''),
    );
    setValue('extraNotes', meta?.extraNotes || '');
  }, [open, isNew, row, customers, formType, isEntitlement, reset, setValue]);

  useEffect(() => {
    if (custvalue?.nationalNo && !getValues('civilId')) {
      setValue('civilId', custvalue.nationalNo);
    }
  }, [custvalue, getValues, setValue]);

  const getDefaultAgreementTitle = () => {
    if (isRTL) {
      return formTypeTitles?.[formType]?.ar || 'مخالصة عميل';
    }
    return formTypeTitles?.[formType]?.en || 'Customer Clearance';
  };

  const getSummary = (data: any) => {
    if (isEntitlement) {
      return (
        data.deliveryDetails ||
        data.extraNotes ||
        (isRTL
          ? 'إقرار استلام المواد المصورة'
          : 'Media delivery acknowledgment')
      );
    }

    if (isFinancialCompensation) {
      return (
        data.reason ||
        data.extraNotes ||
        (isRTL ? 'إقرار تعويض مالي' : 'Financial compensation acknowledgment')
      );
    }

    if (isClearanceWithDeposit) {
      return (
        data.reason ||
        data.extraNotes ||
        (isRTL
          ? 'إقرار إلغاء عقد مع استرجاع عربون'
          : 'Contract cancellation with deposit return')
      );
    }

    return (
      data.reason ||
      data.extraNotes ||
      (isRTL
        ? 'إقرار إلغاء عقد بدون استرجاع مستحقات'
        : 'Contract cancellation without financial return')
    );
  };

  const onSubmit = async (data: any) => {
    if (!custvalue) {
      await errorAlert(setAlrt, isRTL);
      return;
    }

    const normalizedDocumentDate = toJSDate(documentDate);
    const normalizedEventDate = toJSDate(eventDate);

    if (!normalizedDocumentDate) {
      await errorAlert(setAlrt, isRTL);
      return;
    }

    if (requiresEventDate && !normalizedEventDate) {
      await errorAlert(setAlrt, isRTL);
      return;
    }

    setSaving(true);

    const note = buildCustomerFormNote({
      formType,
      civilId: data.civilId,
      agreementNo: data.agreementNo,
      eventDate: toInputDate(normalizedEventDate),
      iban: data.iban,
      reason: data.reason,
      deliveryDetails: data.deliveryDetails,
      extraNotes: data.extraNotes,
      customerPhone: custvalue?.phone || custvalue?.mobile || '',
    });

    const variables: any = {
      _id: row?._id ? row._id : undefined,
      opType,
      time: normalizedDocumentDate,
      title: data.agreementNo || getDefaultAgreementTitle(),
      desc: getSummary(data),
      amount: data.amount ? Number(data.amount) : undefined,
      startDate: normalizedEventDate || undefined,
      note,
      customer: {
        customerId: custvalue?._id,
        customerName: custvalue?.name,
        customerNameAr: custvalue?.nameAr,
        customerPhone: custvalue?.phone || custvalue?.mobile,
      },
    };

    const mutate = isNew ? addAction : editAction;
    const mutateName = isNew ? 'createFormOperation' : 'updateFormOperation';
    await apply(mutate, mutateName, variables);
  };

  const apply = async (mutate: any, mutateName: string, variables: any) => {
    try {
      const res = await mutate({ variables });
      const result = res?.data?.[mutateName];
      if (!result?.ok) {
        const errMsg = result?.error;
        if (errMsg) {
          await errorAlertMsg(setAlrt, `${errMsg}`);
        } else {
          await errorAlert(setAlrt, isRTL);
        }
        setSaving(false);
        return;
      }

      closeModal();
      if (onSaved) {
        await onSaved();
      } else {
        await successAlert(setAlrt, isRTL);
      }
    } catch (error) {
      onError(error);
    }
  };

  const onHandleSubmit = () => {
    handleSubmit(onSubmit)();
  };

  const closeModal = () => {
    onClose();
    setSaving(false);
    setCustvalue(null);
    setEventDate(null);
    setDocumentDate(null);
    reset();
  };

  const onError = async (error: any) => {
    setSaving(false);
    if (error.message.includes('duplicate')) {
      await dublicateAlert(setAlrt, isRTL);
    } else {
      await errorAlert(setAlrt, isRTL);
      reset();
      console.log(error);
    }
  };

  const printData = {
    ...row,
    customer: custvalue,
    custvalue,
    contractDate: eventDate || row?.startDate,
    formType,
    formMeta: {
      ...(parseCustomerFormNote(row?.note) || {}),
      civilId: getValues('civilId') || '',
      agreementNo: getValues('agreementNo') || row?.title || '',
      eventDate: eventDate || row?.startDate || '',
      iban: getValues('iban') || '',
      reason: getValues('reason') || '',
      deliveryDetails: getValues('deliveryDetails') || '',
      extraNotes: getValues('extraNotes') || '',
      customerPhone:
        custvalue?.phone || custvalue?.mobile || row?.customerPhone || '',
    },
    isRTL,
  };

  const getTitle = () => {
    if (isRTL) {
      return formTypeTitles?.[formType]?.ar || 'مخالصة عميل';
    }
    return formTypeTitles?.[formType]?.en || 'Customer Clearance';
  };

  const title = isRTL
    ? isNew
      ? `إضافة ${getTitle()}`
      : `تعديل ${getTitle()}`
    : isNew
      ? `New ${getTitle()}`
      : `Edit ${getTitle()}`;

  return (
    <PopupLayout
      isRTL={isRTL}
      open={open}
      onClose={closeModal}
      title={title}
      onSubmit={onHandleSubmit}
      theme={theme}
      alrt={alrt}
      maxWidth={'md'}
      saving={saving}
    >
      <Grid container spacing={2}>
        <Grid item xs={12}>
          {!isNew && row?._id && (
            <PrintDownloadCustomer
              company={company}
              printData={printData}
              componentRef={componentRef}
              handleReactPrint={handleReactPrint}
              isRTL={isRTL}
              no={row?.docNo}
              isNew={isNew}
            />
          )}
        </Grid>
        <Grid item xs={12}>
          <AutoFieldLocal
            name="customer"
            title={isRTL ? 'العميل' : 'Customer'}
            words={words}
            options={customers}
            value={custvalue}
            setSelectValue={setCustvalue}
            isRTL={isRTL}
            fullWidth
            showphone
          ></AutoFieldLocal>
        </Grid>

        <Grid item xs={6}>
          <CalenderLocal
            isRTL={isRTL}
            label={isRTL ? 'تاريخ الإقرار' : 'Clearance Date'}
            value={documentDate}
            onChange={(date) => setDocumentDate(date)}
            width="100%"
          />
        </Grid>

        {requiresEventDate && (
          <Grid item xs={6}>
            <CalenderLocal
              isRTL={isRTL}
              label={isRTL ? 'تاريخ الحفل/الموعد' : 'Event Date'}
              value={eventDate}
              onChange={(date) => setEventDate(date)}
              width="100%"
            />
          </Grid>
        )}

        <Grid item xs={6}>
          <TextFieldLocal
            name="civilId"
            label={isRTL ? 'رقم البطاقة الشخصية' : 'Civil ID No'}
            register={register}
            errors={errors}
            row={row}
            fullWidth
            mb={0}
          />
        </Grid>

        {!isEntitlement && (
          <Grid item xs={6}>
            <TextFieldLocal
              name="agreementNo"
              label={
                isRTL
                  ? isClearanceNoDeposit
                    ? 'رقم العقد الملغي'
                    : 'رقم عقد الاتفاق'
                  : isClearanceNoDeposit
                    ? 'Cancelled Agreement No'
                    : 'Agreement Contract No'
              }
              register={register}
              errors={errors}
              row={row}
              fullWidth
              mb={0}
            />
          </Grid>
        )}

        {(isFinancialCompensation || isClearanceWithDeposit) && (
          <>
            <Grid item xs={6}>
              <TextFieldLocal
                name="amount"
                label={
                  isFinancialCompensation
                    ? isRTL
                      ? 'قيمة التعويض (ر.ق)'
                      : 'Compensation Amount (QAR)'
                    : isRTL
                      ? 'مبلغ العربون (ر.ق)'
                      : 'Deposit Amount (QAR)'
                }
                register={register}
                errors={errors}
                row={row}
                type="number"
                fullWidth
                mb={0}
              />
            </Grid>
            <Grid item xs={6}>
              <TextFieldLocal
                name="iban"
                label={isRTL ? 'رقم الآيبان للتحويل' : 'Refund/Payment IBAN'}
                register={register}
                errors={errors}
                row={row}
                fullWidth
                mb={0}
              />
            </Grid>
          </>
        )}

        {(isFinancialCompensation ||
          isClearanceWithDeposit ||
          isClearanceNoDeposit) && (
          <Grid item xs={12}>
            <TextFieldLocal
              name="reason"
              label={
                isFinancialCompensation
                  ? isRTL
                    ? 'سبب التعويض / وصف الضرر'
                    : 'Compensation Reason / Damage Description'
                  : isClearanceWithDeposit
                    ? isRTL
                      ? 'سبب إلغاء العقد'
                      : 'Contract Cancellation Reason'
                    : isRTL
                      ? 'سبب إلغاء العقد وعدم الاستحقاق المالي'
                      : 'Cancellation Reason Without Financial Return'
              }
              register={register}
              errors={errors}
              row={row}
              multiline
              rows={2}
              fullWidth
              mb={0}
            />
          </Grid>
        )}

        {isEntitlement && (
          <Grid item xs={12}>
            <TextFieldLocal
              name="deliveryDetails"
              label={
                isRTL
                  ? 'تفاصيل المواد المسلّمة (فيديو/صور)'
                  : 'Delivered Media Details (Video/Photos)'
              }
              register={register}
              errors={errors}
              row={row}
              multiline
              rows={3}
              fullWidth
              mb={0}
            />
          </Grid>
        )}

        <Grid item xs={12}>
          <TextFieldLocal
            name="extraNotes"
            label={
              isRTL ? 'ملاحظات إضافية (اختياري)' : 'Additional Notes (Optional)'
            }
            register={register}
            errors={errors}
            row={row}
            multiline
            rows={2}
            fullWidth
            mb={0}
          />
        </Grid>

        {!isNew && row?._id && (
          <Grid item xs={12} style={{ display: 'none' }}>
            <CustomerClearancePrint
              company={company}
              printData={printData}
              ref={componentRef}
            />
          </Grid>
        )}
      </Grid>
    </PopupLayout>
  );
};

export default PopupCustomerForm;
